import { useEffect, useRef, useState } from 'react';
import { log } from '../lib/logger';

const GEOAPIFY_KEY = process.env.REACT_APP_GEOAPIFY_KEY;
const API = 'https://api.geoapify.com/v1/geocode/autocomplete';
const MIN_QUERY = 3;
const DEBOUNCE_MS = 250;

/**
 * Debounced Geoapify (AU-only) address autocomplete hook.
 * Returns the suggestions array, loading flag, and a manual `fetch(q)` trigger.
 *
 * Keeps the calling component slim — it just renders the input + dropdown.
 */
export const useAddressSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchNow = (q) => {
    if (!GEOAPIFY_KEY) return;
    if (!q || q.trim().length < MIN_QUERY) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    const url = `${API}?text=${encodeURIComponent(q)}&filter=countrycode:au&limit=5&format=json&apiKey=${GEOAPIFY_KEY}`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const results = (data.results || []).map((r) => ({
          formatted: r.formatted,
          city: r.city,
          state: r.state,
          postcode: r.postcode,
          lat: r.lat,
          lon: r.lon,
        }));
        setSuggestions(results);
      })
      .catch((err) => {
        log.warn('Geoapify autocomplete failed', err);
        setSuggestions([]);
      })
      .finally(() => setLoading(false));
  };

  const queueFetch = (q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchNow(q), DEBOUNCE_MS);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return { suggestions, loading, queueFetch, clear: () => setSuggestions([]), keyMissing: !GEOAPIFY_KEY };
};

/**
 * Pure keyboard handler for the autocomplete dropdown. Lifted out so the
 * component body stays small and this logic is unit-testable in isolation.
 */
export const buildAutocompleteKeyHandler = ({
  open, suggestions, highlight, setHighlight, setOpen, pick,
}) => (e) => {
  if (!open || !suggestions.length) return;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    setHighlight((h) => Math.max(h - 1, 0));
  } else if (e.key === 'Enter' && highlight >= 0) {
    e.preventDefault();
    pick(suggestions[highlight]);
  } else if (e.key === 'Escape') {
    setOpen(false);
  }
};
